import { useCallback } from 'react';

import {
  ProductFavorite,
  ProductUUID,
  UpdateProductCacheAction,
  ToggleProductFavoriteGqlQuery,
  ToggleProductFavoriteGqlStatus,
  ToggleProductFavoriteCacheKeys,
  ToggleProductFavoriteGqlError,
  UpdateShowProductCacheAction
} from '../../productsTypes';

import { useUpdateQuery } from '../../../common/reactQuery/hooks/useUpdateQuery';

import { ProductFavoriteButtonProduct } from '../../components/buttons/ProductFavoriteButton';

interface ToggleProductFavoriteInput {
  clientMutationId?: string;
  uuid: ProductUUID;
}

interface ToggleProductFavoriteError {
  fullMessages: ToggleProductFavoriteGqlError;
}

interface ToggleProductFavoriteResponse<ToggleProductFavoriteItemType> {
  toggleProductFavorite: {
    status: ToggleProductFavoriteGqlStatus;
    recordUuid: ProductUUID;
    record: ToggleProductFavoriteItemType;
    errors: ToggleProductFavoriteError;
  };
}

interface ToggleProductFavoriteOptions<ToggleProductFavoriteItemType> {
  query: ToggleProductFavoriteGqlQuery;
  cacheKeys?: ToggleProductFavoriteCacheKeys;
  updateIndexProductCache?: UpdateProductCacheAction<ToggleProductFavoriteItemType>;
  updateShowProductCache?: UpdateShowProductCacheAction<ProductFavoriteButtonProduct>;
}

const action = 'toggleProductFavorite';

interface ToggleProductFavoriteItem {
  favorite: ProductFavorite;
}

function useToggleProductFavorite<
  ToggleProductFavoriteItemType extends ToggleProductFavoriteItem
>({
  query,
  cacheKeys,
  updateIndexProductCache,
  updateShowProductCache
}: ToggleProductFavoriteOptions<ToggleProductFavoriteItemType>) {
  const handleOptimisticUpdate = useCallback<
    (input: ToggleProductFavoriteInput) => null | (() => void)
  >(
    (input) => {
      const updateIndexProductCacheRollback = updateIndexProductCache?.({
        selector: { uuid: input?.uuid },
        updateFunction: (prevProduct) => ({
          ...prevProduct,
          favorite: !prevProduct?.favorite
        })
      });

      const updateShowProductCacheRollback = updateShowProductCache?.(
        (prevProduct) => ({
          ...prevProduct,
          favorite: !prevProduct?.favorite
        })
      );

      return () => {
        updateIndexProductCacheRollback?.();
        updateShowProductCacheRollback?.();
      };
    },
    [updateIndexProductCache, updateShowProductCache]
  );

  const {
    updateQueryData,
    updateQuery,
    updateQueryReset,
    updateQueryError,
    updateQueryLoading,
    updateQueryErrorMessage
  } = useUpdateQuery<
    ToggleProductFavoriteInput,
    ToggleProductFavoriteResponse<ToggleProductFavoriteItemType>,
    ToggleProductFavoriteError,
    ToggleProductFavoriteItemType
  >({
    action,
    cacheKeys,
    query,
    onOptimisticUpdate:
      updateIndexProductCache || updateShowProductCache
        ? handleOptimisticUpdate
        : undefined
  });

  return {
    toggleProductFavoriteData: updateQueryData,
    toggleProductFavoriteError: updateQueryError,
    toggleProductFavoriteLoading: updateQueryLoading,
    toggleProductFavoriteErrorMessage: updateQueryErrorMessage,
    toggleProductFavorite: updateQuery,
    toggleProductFavoriteReset: updateQueryReset
  };
}

export default useToggleProductFavorite;
